import {
  type ComponentType,
  type ReactNode,
  createContext,
  memo,
  use,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import type { TradeExecutionState } from '../hooks/useTradeExecution'
import type { TradeLifecycleToastHandle } from '../hooks/useTradeLifecycleToast'
import { createPreloadedModule } from '../lib/preloadedModule'

// The light half of the trade session: the context, its hooks and the host App
// mounts above the routes. Nothing here imports the trade client by value, so
// it stays out of the boot graph (SearchModal.listBuy.test › the boot graph);
// the executor itself (PairTradeSessionContext) is loaded with import().

/** A launched click's toast, following ITS attempt's record to the verdict. */
export type AttemptToast = {
  handle: TradeLifecycleToastHandle
  label: string
  failContent: (reason: string) => ReactNode
}

export type PairTradeSessionValue = {
  exec: TradeExecutionState
  /** Hands a launched click's toast to the session: it is driven by the
      attempt's record from here on, whatever happens to the surface that
      started it (a token switch, a route change, a closed panel). */
  bindAttemptToast: (attemptKey: string, toast: AttemptToast) => void
  /** Is this toast already following an attempt? */
  attemptToastBound: (handle: TradeLifecycleToastHandle) => boolean
  /**
   * Every surface launches a click through here. The toast leaves the
   * starter's ownership at once (its unmount no longer ends it), binds to the
   * attempt the executor registers, and a launch that REJECTS ends it as
   * failed. Resolves with the attempt key, or null when none was registered
   * (the starter decides that verdict while it is on screen).
   */
  launchAttempt: (run: Promise<string | null>, toast: AttemptToast) => Promise<string | null>
}

export const PairTradeSessionCtx = createContext<PairTradeSessionValue | null>(null)

/** Set by the lazy host while its executor has not published a value yet. */
const PendingCtx = createContext<Promise<void> | null>(null)

export type PairTradeSessionEngineProps = { publish: (value: PairTradeSessionValue) => void }

// The executor module, loaded once. memo: the executor's value is a new object
// on every render of its own, and each publish re-renders the host; without
// memo the host's re-render would re-render the engine, which publishes again
// — a loop. With it the engine renders only for its own state and contexts.
type Engine = ComponentType<PairTradeSessionEngineProps>
// Every host hears ANY successful load: a load that failed at boot is retried
// by the pair page's preload (createPreloadedModule forgets a failure), and the
// host must take that one too, or the page would wait for ever.
const engineListeners = new Set<(engine: Engine) => void>()
const engineModule = createPreloadedModule(() =>
  import('./PairTradeSessionContext').then((module) => {
    const engine: Engine = memo(module.PairTradeSessionEngine)
    for (const listener of engineListeners) listener(engine)
    return engine
  })
)

/** Starts (or joins) the executor's load. The pair page's preload waits for
    it too, so a token page never renders its trading surfaces before the
    executor is in hand. */
export const loadPairTradeSessionEngine = () => engineModule.load()

/**
 * The app's trade session, above the routes. Its tree never changes shape:
 * the executor arrives as a SIBLING of the routes and publishes its value up,
 * so its arrival does not remount the router. Until the first value, a
 * surface that needs the session waits (Suspense) instead of throwing.
 */
export function PairTradeSessionHost({ children }: { children: ReactNode }) {
  const [ready] = useState(() => {
    let resolve = () => {}
    const promise = new Promise<void>((r) => {
      resolve = r
    })
    return { promise, resolve }
  })
  const [value, setValue] = useState<PairTradeSessionValue | null>(null)
  const [Engine, setEngine] = useState<Engine | null>(() => engineModule.peek())

  useEffect(() => {
    const take = (loaded: Engine) => setEngine(() => loaded)
    engineListeners.add(take)
    const loaded = engineModule.peek()
    if (loaded) take(loaded)
    else
      engineModule
        .load()
        // A failed load leaves the trading surfaces waiting until a later load
        // (the pair page's preload) succeeds; the rest of the app keeps working
        // (the reason the client is not in the boot graph).
        .catch((err: unknown) => console.error('trade session: the executor did not load', err))
    return () => {
      engineListeners.delete(take)
    }
  }, [])

  const publish = useCallback(
    (next: PairTradeSessionValue) => {
      setValue(next)
      ready.resolve()
    },
    [ready]
  )

  return (
    <PendingCtx.Provider value={value ? null : ready.promise}>
      <PairTradeSessionCtx.Provider value={value}>
        {Engine ? <Engine publish={publish} /> : null}
        {children}
      </PairTradeSessionCtx.Provider>
    </PendingCtx.Provider>
  )
}

function useSessionOrPending(): PairTradeSessionValue | null {
  const value = useContext(PairTradeSessionCtx)
  const pending = useContext(PendingCtx)
  // Inside the host before its executor published: wait for it.
  if (!value && pending) use(pending)
  return value
}

export function usePairTradeSession(): PairTradeSessionValue {
  const value = useSessionOrPending()
  if (!value) throw new Error('usePairTradeSession must be used inside <PairTradeSessionProvider>')
  return value
}

export function useOptionalPairTradeSession(): PairTradeSessionValue | null {
  return useSessionOrPending()
}
